import { useStableCallback } from "@prismicio/editor-support/React"
import { clsx } from "clsx"
import {
  type AnimationEvent,
  type CSSProperties,
  type ReactNode,
  useEffect,
  useId,
  useRef
} from "react"

import { theme, type ThemeKeys } from "../../theme"
import styles from "./AnimatedList.module.css"
import {
  type RenderDragPreview,
  type RenderDropIndicator,
  useDndList,
  useDndListItem,
  type UseDndListItemProps
} from "./dnd"
import { getMainDataProps, type State, useLayout } from "./layout"

/*
 * A list of elements.
 * An animation plays for elements getting added, removed or moved.
 * items can optionally be dragged & dropped, if "onReorder" is provided.
 */

/*
 * Implementation notes (i.e, why write this ourselves?)
 *
 * React transition Group is underpowered and doesn't deal with moving items at all, which is the hardest part.
 * react-flip-toolkit: Very complex API, very hard to use (flipKey, flipId, inverseFlipId, etc) and couldn't get its animation to feel good. Also, JS based animations, not ideal.
 * react-spring: Solid lib but out of the box can only deal with moving items if we give them an absolute position AND hardcoded height. We do have lists with variable height items, like Group, etc. Unclear how much work it would be to use that together with ResizeObserver, etc.
 * On top of all this, adding other features on top, like drag & drop makes using libraries even harder.
 */

export interface AnimatedListProps<ITEM> {
  ariaLabel: string
  gap?: Gap
  direction?: "horizontal" | "vertical"
  items: ITEM[]
  getItemKey: (item: ITEM) => string
  /** A pure function used to render each item. */
  children: ChildrenFunction<ITEM>
  animationDuration?: AnimationDuration
  stacked?: {
    /** Offset between stacked items */
    offset: number
    /** Scaling factor between items */
    scaleFactor: number
    /** Maximum number of items to stack, others will be hidden */
    maxDepth: number
  }
  /**
   * When provided, enables drag & drop.
   * The bare minimum is to provide "onReorder" so you can actually re-order your data once the dnd has finished.
   * Drag & Drop can only be initiated from row zones that have the "draggable" attribute.
   */
  dnd?: {
    scrollOffsetTop?: number
    onReorder?: (items: ITEM[]) => void
    onDragChange?: (isDragging: boolean) => void
    renderDropIndicator?: RenderDropIndicator
    renderDragPreview?: RenderDragPreview<ITEM>
  }
}

export function AnimatedList<ITEM>(props: AnimatedListProps<ITEM>) {
  const {
    ariaLabel,
    gap = 0,
    direction = "vertical",
    items,
    getItemKey,
    children,
    animationDuration = 300,
    stacked,
    dnd
  } = props

  const containerRef = useRef<HTMLUListElement>(null)
  const listId = useId()

  const { itemStates, onAnimationEnd } = useLayout({
    containerRef,
    items,
    getItemKey
  })

  const { dragState, preview } = useDndList({
    ...dnd,
    containerRef,
    items,
    itemStates,
    direction,
    listId
  })

  const isDragging = dragState?.dragIndex !== undefined

  const onDragChange = useStableCallback(dnd?.onDragChange)

  useEffect(() => {
    onDragChange?.(isDragging)
  }, [onDragChange, isDragging])

  return (
    <ul
      aria-label={ariaLabel}
      ref={containerRef}
      className={clsx(styles.animatedList, styles[`direction-${direction}`])}
    >
      {itemStates.map((itemState) => {
        // This data item exists but we don't want to render it just yet.
        if (itemState.state === "hidden") return null

        const stackingProps = getStackingProps({
          listLength: itemStates.length,
          stacked,
          index: itemState.index
        })

        if (stackingProps?.outsideMaxDepth) return null

        return (
          <AnimatedListItem
            key={itemState.key}
            listId={listId}
            gap={gap}
            direction={direction}
            itemState={itemState}
            dragState={dragState}
            stackingStyle={stackingProps?.style}
            animationDuration={animationDuration}
            renderDropIndicator={dnd?.renderDropIndicator}
            onAnimationEnd={onAnimationEnd}
          >
            {children}
          </AnimatedListItem>
        )
      })}
      {preview}
    </ul>
  )
}

interface AnimatedListItemProps<ITEM> extends UseDndListItemProps<ITEM> {
  gap: Gap
  direction: "horizontal" | "vertical"
  children: ChildrenFunction<ITEM>
  stackingStyle?: CSSProperties
  animationDuration: AnimationDuration
  onAnimationEnd: (evt: AnimationEvent<HTMLElement>) => void
  listId: string
}

function AnimatedListItem<ITEM>(props: AnimatedListItemProps<ITEM>) {
  const {
    gap,
    direction,
    itemState,
    children,
    stackingStyle,
    animationDuration,
    onAnimationEnd
  } = props

  const { isDragging, rowProps, beforeDropIndicator, afterDropIndicator } =
    useDndListItem(props)

  const rowClassName = clsx(styles.row, styles[`direction-${direction}`])

  const mainClassName = clsx(
    styles.item,
    styles[itemState.state],
    isDragging && styles.dragging
  )

  const mainStyle = getItemStyles(itemState, animationDuration)

  // Use a spacer instead of an actual gap so that the mouse pointer
  // is always above a given list item and not empty space, which simplifies
  // the dnd implementation when finding the nearest dropIndicator.
  const spacer = (
    <div
      style={{ marginTop: theme.space[gap], marginLeft: theme.space[gap] }}
      aria-hidden
    />
  )

  return (
    <li {...rowProps} className={rowClassName} style={stackingStyle}>
      {beforeDropIndicator}
      <div
        {...getMainDataProps(itemState)}
        className={mainClassName}
        style={mainStyle}
        onAnimationEnd={onAnimationEnd}
      >
        {children(itemState.item, itemState.index, itemState.state)}
      </div>
      {afterDropIndicator}
      {spacer}
    </li>
  )
}

type Gap = ThemeKeys<"space">
type AnimationDuration = ThemeKeys<"transitionDuration">

type ChildrenFunction<ITEM> = (
  item: ITEM,
  index: number,
  state: State<ITEM>["state"]
) => ReactNode

interface GetStackingPropsArgs<ITEM> {
  listLength: number
  stacked: AnimatedListProps<ITEM>["stacked"]
  index: number
}

function getItemStyles<ITEM>(
  itemState: State<ITEM>,
  animationDuration: AnimationDuration
): CSSProperties {
  const duration = theme.transitionDuration[animationDuration]

  const delay = Boolean(
    itemState.state === "entering" && itemState.animationDelay
  )

  const transitionDuration =
    itemState.state === "present" ? duration : undefined

  const transitionDelay = delay ? duration : undefined

  return {
    animationDuration: duration,
    transitionDuration,
    transitionDelay,
    ...itemState.style
  }
}

function getStackingProps<ITEM>(args: GetStackingPropsArgs<ITEM>) {
  const { listLength, stacked, index } = args

  if (!stacked) return undefined

  // Item is outside of the maximum stacking depth and so should be hidden as it would otherwise be too small to see.
  const outsideMaxDepth = index < listLength - stacked.maxDepth
  const topOffset = (listLength - index) * stacked.offset
  const scaleFactor = 1 - (listLength - index) * stacked.scaleFactor

  const style: CSSProperties = {
    top: topOffset,
    transform: `scale(${scaleFactor})`,
    position: "absolute",
    transitionDuration: "100ms"
  }

  return {
    style,
    outsideMaxDepth
  }
}
